import { useState } from 'react';
import type { FormEvent, ReactElement } from 'react';
import { Link } from 'react-router';
import type { FellowNoteSummary, ImportRun } from '@contracts/imports';
import { useCancelImport, useFellowPreview, useImportRun, useStartImport } from '../api/queries';
import { Button, Card, Checkbox, Field, Icon, Pill, Switch, useNotices } from '../ui';
import { SettingsLayout } from '../settings/SettingsLayout';
import { dayLabel } from '../lib/time';

/**
 * Bringing a history in from Fellow (PRD 37).
 *
 * Three steps, and the middle one is the point: **look before you import**.
 * A tool that asks for a key and then does something to two years of meetings
 * is a tool nobody presses twice. So the key buys a list, the list has ticks,
 * and nothing is written until somebody has chosen.
 */
export function ImportPage(): ReactElement {
  return (
    <SettingsLayout
      title="Bring your meetings across"
      sub="Fellow — their transcripts, their notes, and what we can work out from them"
    >
      <Bring />
    </SettingsLayout>
  );
}

function Bring(): ReactElement {
  const [workspace, setWorkspace] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [since, setSince] = useState('');
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [notes, setNotes] = useState<FellowNoteSummary[]>([]);
  const [account, setAccount] = useState<string | null>(null);
  /** Whether a look has come back at all — "no meetings" and "not looked yet" are different states. */
  const [looked, setLooked] = useState(false);
  const [cursor, setCursor] = useState<string | null>(null);
  const [read, setRead] = useState(true);
  const [runId, setRunId] = useState<string | null>(null);

  const preview = useFellowPreview();
  const start = useStartImport();
  const run = useImportRun(runId);
  const { show } = useNotices();

  const look = (from: string | null) => {
    preview.mutate(
      { workspace, apiKey, cursor: from, since: since ? `${since}T00:00:00.000Z` : null },
      {
        onSuccess: (page) => {
          setLooked(true);
          setAccount(page.account);
          setNotes((held) => (from ? [...held, ...page.notes] : page.notes));
          setCursor(page.cursor);
        },
        onError: (err) => show({ tone: 'failure', text: err.message }),
      },
    );
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setNotes([]);
    setPicked(new Set());
    look(null);
  };

  const toggle = (id: string) =>
    setPicked((held) => {
      const next = new Set(held);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const takeable = notes.filter((note) => !note.alreadyImported);
  const bring = () => {
    start.mutate(
      {
        workspace,
        apiKey,
        noteIds: [...picked],
        spaceId: null,
        read,
      },
      {
        onSuccess: (started) => {
          setRunId(started.id);
          setApiKey('');
          show({ tone: 'success', text: `Bringing ${started.total} meeting(s) across.` });
        },
        onError: (err) => show({ tone: 'failure', text: err.message }),
      },
    );
  };

  if (runId && run.data) return <Progress run={run.data} onDone={() => setRunId(null)} />;

  return (
    <div className="stack-lg">
      <Card title="Your Fellow">
        <form onSubmit={submit} className="stack" style={{ gap: 12, maxWidth: 520 }}>
          <Field
            label="Your Fellow address"
            placeholder="yourcompany.fellow.app"
            value={workspace}
            onChange={(e) => setWorkspace(e.target.value)}
            hint="The address you open Fellow at."
          />
          <Field
            label="An API key"
            type="password"
            autoComplete="off"
            placeholder="fellow_…"
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            hint="In Fellow: your user settings → Developer API → generate a key. It reaches exactly what you reach, it is used for this import, and it is not kept afterwards."
          />
          <Field
            label="Only meetings since"
            type="date"
            value={since}
            onChange={(e) => setSince(e.target.value)}
            hint="Leave it empty for everything, newest first."
          />
          <div className="cluster" style={{ gap: 8 }}>
            <Button
              type="submit"
              variant="primary"
              disabled={!workspace.trim() || apiKey.trim().length < 8}
              working={preview.isPending && notes.length === 0 ? 'Looking…' : false}
            >
              Look at what is there
            </Button>
            {account ? <span className="t-secondary">signed in as {account}</span> : null}
          </div>
        </form>
      </Card>

      {looked && notes.length === 0 ? (
        <Card title="Nothing came back">
          <div className="stack" style={{ gap: 10, maxWidth: 560 }}>
            <p className="t-body">
              Fellow answered, and {account ? <strong>{account}</strong> : 'that key'} has no
              meetings we can see. The request worked — this is Fellow saying the list is empty, not
              an error.
            </p>
            <p className="t-secondary">
              Three things make that happen: the key belongs to a different workspace than the
              address above; the account genuinely has no notes; or a date was set that excludes
              them all. A key reaches exactly what its owner reaches in Fellow, so if you can see
              meetings there and not here, it is the first one.
            </p>
          </div>
        </Card>
      ) : null}

      {notes.length > 0 ? (
        <Card
          title={`What is in there — ${notes.length} meeting${notes.length === 1 ? '' : 's'}`}
          aside={
            <div className="cluster" style={{ gap: 8 }}>
              <Button
                variant="quiet"
                size="sm"
                onClick={() => setPicked(new Set(takeable.map((n) => n.id)))}
              >
                Tick all {takeable.length}
              </Button>
              <Button variant="quiet" size="sm" onClick={() => setPicked(new Set())}>
                None
              </Button>
            </div>
          }
          flush
        >
          <div className="rows">
            {notes.map((note) => (
              <div key={note.id} className="row dense">
                <span className="row-main">
                  {note.alreadyImported ? (
                    <span className="who">
                      <Icon name="check" size={12} /> {note.title}
                    </span>
                  ) : (
                    <Checkbox
                      label={note.title}
                      checked={picked.has(note.id)}
                      onChange={() => toggle(note.id)}
                    />
                  )}
                  <span className="row-sub">
                    <span className="mono">
                      {note.startedAt ? dayLabel(note.startedAt) : 'no date'} ·{' '}
                      {note.attendees.length} there
                    </span>
                    {note.hasRecording ? (
                      <Pill tone="green">has a recording</Pill>
                    ) : (
                      <Pill>notes only</Pill>
                    )}
                    {note.alreadyImported ? <Pill>already here</Pill> : null}
                  </span>
                </span>
              </div>
            ))}
          </div>
          {cursor ? (
            <div style={{ padding: 12 }}>
              <Button
                variant="quiet"
                onClick={() => look(cursor)}
                working={preview.isPending ? 'Looking…' : false}
              >
                Show more
              </Button>
            </div>
          ) : null}
        </Card>
      ) : null}

      {picked.size > 0 ? (
        <Card title={`Bring ${picked.size} across`}>
          <div className="stack" style={{ gap: 12, maxWidth: 520 }}>
            {/* They land as yours alone; post them to a channel afterwards
                (PRD 58) — a channel is a sharing, and sharing is a decision
                made meeting by meeting or series by series, not in bulk here. */}
            <div className="stack" style={{ gap: 4 }}>
              <Switch
                label="Read them for commitments and knowledge"
                checked={read}
                onChange={(e) => setRead(e.target.checked)}
              />
              <span className="t-secondary">
                What Fellow already wrote comes across as the note. This is the part it did not
                have: who owes what, and what the company decided. It is a model call for each
                meeting, so it costs a few cents each.
              </span>
            </div>
            <div className="cluster" style={{ gap: 8 }}>
              <Button
                variant="primary"
                onClick={bring}
                working={start.isPending ? 'Starting…' : false}
              >
                Bring them across
              </Button>
              <span className="t-secondary">
                Nobody else is given these meetings; share them afterwards as you would your own.
              </span>
            </div>
          </div>
        </Card>
      ) : null}
    </div>
  );
}

/** While it runs, and after: what arrived, what was already here, what would not come. */
function Progress({ run, onDone }: { run: ImportRun; onDone: () => void }): ReactElement {
  const cancel = useCancelImport();
  const { show } = useNotices();
  const working = run.state === 'QUEUED' || run.state === 'RUNNING';
  const done = run.imported + run.skipped + run.failures.length;
  const percent = run.total > 0 ? Math.round((done / run.total) * 100) : 0;

  return (
    <div className="stack-lg">
      <Card title={working ? 'Bringing them across' : 'What came across'}>
        <div className="stack" style={{ gap: 12 }}>
          <p className="mono cluster" style={{ gap: 10, flexWrap: 'wrap' }}>
            <span>
              {done} of {run.total}
            </span>
            <span>· {run.imported} brought in</span>
            {run.skipped > 0 ? <span>· {run.skipped} already here</span> : null}
            {run.failures.length > 0 ? <span>· {run.failures.length} would not come</span> : null}
            <Pill
              tone={run.state === 'FAILED' ? 'red' : run.state === 'DONE' ? 'green' : undefined}
            >
              {run.state === 'QUEUED'
                ? 'waiting to start'
                : run.state === 'RUNNING'
                  ? 'working'
                  : run.state === 'DONE'
                    ? 'finished'
                    : run.state === 'CANCELLED'
                      ? 'stopped'
                      : 'failed'}
            </Pill>
          </p>
          <div
            className="progress"
            role="progressbar"
            aria-valuenow={percent}
            aria-valuemin={0}
            aria-valuemax={100}
          >
            <span style={{ width: `${percent}%` }} />
          </div>
          {run.error ? <p className="t-secondary">{run.error}</p> : null}
          <div className="cluster" style={{ gap: 8 }}>
            {working ? (
              <Button
                onClick={() =>
                  cancel.mutate(run.id, {
                    onSuccess: () =>
                      show({ tone: 'success', text: 'Stopped. What had arrived is yours.' }),
                  })
                }
              >
                Stop
              </Button>
            ) : (
              <>
                <Link to="/meetings" className="btn btn-primary">
                  Open the meetings
                </Link>
                <Button variant="quiet" onClick={onDone}>
                  Bring some more
                </Button>
              </>
            )}
            {run.read ? (
              <span className="t-secondary">
                Reading them for commitments and knowledge happens in the background; what it finds
                appears on each meeting and on <Link to="/knowledge">Knowledge</Link>.
              </span>
            ) : null}
          </div>
        </div>
      </Card>

      {run.failures.length > 0 ? (
        <Card title="These would not come" flush>
          <div className="rows">
            {run.failures.map((failure) => (
              <div key={failure.noteId} className="row dense">
                <span className="row-main">
                  <span className="row-title">{failure.title || failure.noteId}</span>
                  <span className="row-sub">
                    <span className="mono">{failure.reason}</span>
                  </span>
                </span>
              </div>
            ))}
          </div>
        </Card>
      ) : null}
    </div>
  );
}
